export default {
  slug: "/dental-veneers",
  group: { href: "/dental", label: { en: "Dental care", ar: "طب الأسنان" } },
  heroImage: "/assets/images/ai/services/dental/emax.webp",

  en: {
    title: "Porcelain Veneers (E-max) in Istanbul",
    eyebrow: "Dental care",
    lead:
      "A veneer is a thin ceramic facing bonded to the front of a tooth. The aim is to change shape and " +
      "colour while removing as little enamel as possible — on a minimally prepared tooth that is roughly " +
      "0.3–0.8 mm, and it does not grow back.",
    cta: "Book a free assessment",
    heroAlt: "Porcelain veneer consultation at Platinya Clinic",
    stats: [
      { value: "2 visits", label: "At the clinic" },
      { value: "0.3–0.8 mm", label: "Enamel usually reduced" },
      { value: "10 years+", label: "Typical lifespan" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "Veneers solve problems of shape, colour and position in the front teeth. They do not solve a bite " +
        "problem, and they do not straighten crowded teeth as well as orthodontics does.",
      items: [
        "Chipped, worn or slightly rotated front teeth where the change wanted is cosmetic",
        "Discolouration inside the tooth — fluorosis, old tetracycline staining, a root-treated tooth — that whitening cannot lift",
        "Small gaps and uneven edges; a single midline gap can often be closed",
        "Sound enamel: a veneer needs something healthy to bond to",
      ],
      note:
        "Where crowding is significant, orthodontics first is usually the better plan: fewer teeth are cut " +
        "and the result lasts longer. Veneers over crowded teeth end up thick, opaque and hard to clean.",
    },
    techniques: {
      heading: "Which ceramic, and why",
      intro:
        "The material is chosen by how much tooth is missing, how much enamel can be kept and whether the " +
        "case is one tooth or a full smile.",
      items: [
        {
          name: "E-max — lithium disilicate",
          text:
            "The default for most veneers: pressed or milled lithium disilicate, bonded with resin cement. " +
            "Strong enough for front teeth and premolars, translucent enough to behave like enamel, and it " +
            "asks for about 0.3–0.8 mm of reduction rather than a full crown.",
        },
        {
          name: "Feldspathic ceramic",
          text:
            "Hand-layered by a ceramist instead of milled, so colour and translucency are built in layers. " +
            "The most lifelike option for a single visible tooth; also thinner, more technique-sensitive and " +
            "more expensive.",
        },
        {
          name: "Composite bonding",
          text:
            "Resin built directly on the tooth, no enamel removed, finished in one visit and reversible. It " +
            "stains and chips sooner — five to seven years is realistic — so it suits small repairs or a " +
            "trial of the shape before committing to ceramic.",
        },
      ],
    },
    journey: {
      heading: "From consultation to bonded veneers",
      steps: [
        {
          name: "Assessment",
          text:
            "Photographs, x-rays, shade and bite. The first question is whether orthodontics would give a " +
            "better result; the second is how much enamel there is to work with.",
        },
        {
          name: "Preparation",
          text:
            "Under local anaesthetic the enamel is reduced by the thickness of the planned ceramic, a scan " +
            "is taken, and temporary veneers are fitted so the teeth are not left exposed or sensitive.",
        },
        {
          name: "Try-in",
          text:
            "The ceramic is tried in with water-soluble paste before anything is bonded. Fit, colour and " +
            "shape are checked in daylight, not only under the surgery light.",
        },
        {
          name: "Bonding",
          text:
            "Under rubber dam the enamel is etched, the veneer is bonded with resin cement, excess is cleared " +
            "from the margins and the bite is refined. This part is not reversible.",
        },
        {
          name: "Review and maintenance",
          text:
            "A check at one to two weeks, a night guard if you grind, and cleaning instructions that include " +
            "floss at the margin. Nails, ice and pen caps are what break veneers.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro:
        "The procedure is small but the trade is permanent. These are the limits worth understanding before " +
        "the first tooth is prepared.",
      items: [
        "Enamel loss is permanent: the tooth will always need a veneer, a crown or a composite",
        "Sensitivity to cold, sometimes for weeks, especially on thin preparations",
        "Debonding or chipping, usually repairable — though a composite repair never matches perfectly",
        "A single veneer is hard to match to neighbouring teeth years later, when those teeth have aged or changed colour",
        "Gum recession at the margin exposing the edge, or pulp inflammation if preparation is too aggressive",
      ],
      note:
        "The strongest argument for conservative preparation is not the first five years but the third " +
        "replacement: each remake removes a little more tooth, and there is a floor to how much can go.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "How much tooth is removed?",
          a:
            "Between 0.3 and 0.8 mm on a typical E-max veneer, and sometimes none at all where the tooth is " +
            "already worn. The number is set by the thickness the ceramic needs to be strong and by how far " +
            "the tooth has to move visually.",
        },
        {
          q: "Do veneers stain?",
          a:
            "The ceramic itself does not absorb colour the way resin does. What changes is the margin: if " +
            "the junction with the tooth is exposed or poorly cleaned, it darkens. Polishing at routine " +
            "visits keeps it looking clean.",
        },
        {
          q: "Will they look bulky?",
          a:
            "Only if too much is added. A veneer can make a tooth longer or wider, but it cannot make it " +
            "thinner than the tooth already is, so a prominent tooth needs preparation rather than more " +
            "ceramic. This is settled at the design stage, not after bonding.",
        },
        {
          q: "How long do they last?",
          a:
            "Ten years and often more for well-made, well-maintained veneers. Failures are usually chips at " +
            "the incisal edge or leakage at the margin, and both are more common in grinders without a night " +
            "guard.",
        },
      ],
    },
    ctaBand: {
      heading: "Send a photograph of the tooth you want changed",
      text:
        "A close, focused photograph and a front view are enough to say whether a veneer, bonding or " +
        "orthodontics is the more sensible route, and what the preparation would cost you in enamel.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability can only be judged after examining the teeth, " +
      "the gums, the bite and the medical history; results vary between patients.",
  },

  ar: {
    title: "قشور الأسنان الخزفية (E-max) في إسطنبول",
    eyebrow: "طب الأسنان",
    lead:
      "القشرة واجهة خزفية رقيقة تُلصق على الوجه الأمامي للسن. والهدف تغيير الشكل واللون مع إزالة أقل قدر " +
      "ممكن من المينا — نحو ٠٫٣ إلى ٠٫٨ ملم في التحضير المتحفّظ — وهذه المينا لا تعود.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "استشارة قشور الأسنان الخزفية في عيادة بلاتينيا",
    stats: [
      { value: "زيارتان", label: "في العيادة" },
      { value: "٠٫٣–٠٫٨ ملم", label: "المينا المُزالة عادةً" },
      { value: "١٠ سنوات فأكثر", label: "العمر المعتاد" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "القشور تعالج مشاكل الشكل واللون والموضع في الأسنان الأمامية. لكنها لا تعالج مشكلة الإطباق، ولا " +
        "تستقيم الأسنان المزدحمة كما يفعل التقويم.",
      items: [
        "أسنان أمامية مكسورة أو متآكلة أو مائلة قليلاً، والتغيير المطلوب تجميلي",
        "تغيّر اللون من داخل السن — التفلور أو تصبّغ قديم بالتتراسيكلين أو سن معالَج العصب — لا يرفعه التبييض",
        "فراغات صغيرة وحواف غير متساوية؛ وكثيراً ما يمكن إغلاق الفراغ الوسطي الواحد",
        "مينا سليمة: فالقشرة تحتاج سطحاً صحياً لتلتصق به",
      ],
      note:
        "حين يكون الازدحام واضحاً، يكون التقويم أولاً هو الخطة الأفضل عادةً: أسنان أقل برداً ونتيجة أطول " +
        "عمراً. أما القشور فوق أسنان مزدحمة فتكون سميكة ومعتمة وصعبة التنظيف.",
    },
    techniques: {
      heading: "أي خزف، ولماذا",
      intro:
        "يُختار الخزف بحسب مقدار ما فُقد من السن، ومقدار المينا التي يمكن الحفاظ عليها، وهل الحالة سناً " +
        "واحداً أم ابتسامة كاملة.",
      items: [
        {
          name: "E-max — ثنائي سيليكات الليثيوم",
          text:
            "الخيار المعتاد لمعظم القشور: ثنائي سيليكات الليثيوم مضغوطاً أو محفوراً، ويُلصق بإسمنت راتنجي. " +
            "قوّته تكفي الأسنان الأمامية والضواحك، وشفافيته تجعله يتصرّف كالمينا، ويحتاج نحو ٠٫٣–٠٫٨ ملم من " +
            "البرد لا تلبيسة كاملة.",
        },
        {
          name: "الخزف الفلدسباري",
          text:
            "يُبنى بيد الفنّي طبقة فوق طبقة بدل الحفر الآلي، فيُبنى اللون والشفافية على مراحل. وهو الأقرب " +
            "إلى الطبيعية لسن واحد ظاهر؛ لكنه أرقّ وأكثر حساسية لمهارة الفنّي وأعلى كلفة.",
        },
        {
          name: "التجميل بالراتنج المركّب",
          text:
            "يُبنى الراتنج مباشرة على السن دون إزالة مينا، وينتهي في زيارة واحدة وقابل للتراجع. لكنه يتصبّغ " +
            "ويتكسّر أسرع — خمس إلى سبع سنوات واقعية — لذا يناسب الترميمات الصغيرة أو تجربة الشكل قبل " +
            "الالتزام بالخزف.",
        },
      ],
    },
    journey: {
      heading: "من الاستشارة إلى لصق القشور",
      steps: [
        {
          name: "التقييم",
          text:
            "صور وأشعة سينية وتسجيل للون وفحص للإطباق. والسؤال الأول: هل يعطي التقويم نتيجة أفضل؟ " +
            "والثاني: كم مينا متاحة للعمل عليها؟",
        },
        {
          name: "التحضير",
          text:
            "تحت تخدير موضعي تُبرد المينا بمقدار سماكة الخزف المخطّط، ويُؤخذ مسح، وتُركَّب قشور مؤقتة كي " +
            "لا تبقى الأسنان مكشوفة أو حساسة.",
        },
        {
          name: "التجربة",
          text:
            "يُجرَّب الخزف بمعجون قابل للذوبان في الماء قبل أي لصق. وتُفحص الملاءمة واللون والشكل في ضوء " +
            "النهار لا تحت ضوء العيادة وحده.",
        },
        {
          name: "اللصق",
          text:
            "تحت العزل المطاطي تُحفَّر المينا وتُلصق القشرة بإسمنت راتنجي، وتُنظَّف الحواف من الفائض ويُضبط " +
            "الإطباق. وهذه المرحلة لا رجعة فيها.",
        },
        {
          name: "المراجعة والصيانة",
          text:
            "فحص بعد أسبوع أو أسبوعين، وواقٍ ليلي إن كنت تعاني صرير الأسنان، وإرشادات تنظيف تشمل الخيط عند " +
            "الحواف. الأظافر والثلج وأغطية الأقلام هي ما تكسر القشور.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro:
        "الإجراء صغير لكن المقابل دائم. وهذه حدوده التي يجب فهمها قبل برد السن الأول.",
      items: [
        "فقد المينا دائم: سيحتاج السن دائماً إلى قشرة أو تلبيسة أو حشوة تجميلية",
        "حساسية للبارد، أحياناً لأسابيع، خصوصاً في التحضير الرقيق",
        "انفصال القشرة أو كسرها، وهو قابل للإصلاح عادةً — لكن الإصلاح بالراتنج لا يطابق الأصل تماماً",
        "صعوبة مطابقة قشرة واحدة مع الأسنان المجاورة بعد سنوات، حين تتغيّر تلك الأسنان أو يتغيّر لونها",
        "انحسار اللثة عند الحافة فتنكشف حدود القشرة، أو التهاب اللبّ إذا كان التحضير عدوانياً",
      ],
      note:
        "أقوى حجّة للتحضير المتحفّظ ليست السنوات الخمس الأولى بل الاستبدال الثالث: كل إعادة تأخذ مزيداً من " +
        "السن، وهناك حدّ لا يمكن النزول تحته.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "كم يُزال من السن؟",
          a:
            "بين ٠٫٣ و٠٫٨ ملم في قشرة E-max المعتادة، وأحياناً لا شيء إذا كان السن متآكلاً أصلاً. ويحدّد " +
            "المقدار سماكة يحتاجها الخزف ليكون قوياً، ومقدار ما يجب أن يتحرّكه السن بصرياً.",
        },
        {
          q: "هل تتصبّغ القشور؟",
          a:
            "الخزف نفسه لا يمتصّ اللون كما يفعل الراتنج. الذي يتغيّر هو الحافة: إذا انكشف التقاء القشرة " +
            "بالسن أو لم يُنظَّف جيداً اسودّ. والتلميع في الزيارات الدورية يحفظ نظافته.",
        },
        {
          q: "هل ستبدو ضخمة؟",
          a:
            "فقط إذا أُضيف أكثر من اللازم. القشرة تطيل السن أو تعرّضه، لكنها لا تجعله أنحف مما هو عليه، " +
            "فالسن البارز يحتاج تحضيراً لا مزيداً من الخزف. وهذا يُحسم في مرحلة التصميم لا بعد اللصق.",
        },
        {
          q: "كم تدوم؟",
          a:
            "عشر سنوات وأكثر غالباً للقشور الجيدة الصنع والمُعتنى بها. والإخفاقات عادةً كسر في الحدّ القاطع " +
            "أو تسرّب عند الحافة، وكلاهما أكثر شيوعاً عند من يعاني صرير الأسنان دون واقٍ ليلي.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورة للسن الذي تريد تغييره",
      text:
        "صورة قريبة واضحة وصورة أمامية تكفيان لنقول هل القشرة أم الراتنج أم التقويم هو الطريق الأسلم، " +
        "وكم سيكلّفك التحضير من المينا.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يمكن الحكم على الملاءمة إلا بعد فحص الأسنان واللثة والإطباق " +
      "والتاريخ المرضي؛ والنتائج تختلف من مريض لآخر.",
  },
};
